import { useMemo } from 'react';
import { BarChart as BarChartIcon } from 'lucide-react';
import { useAppStore } from '@/store/store';
import { EmptyState } from '@/components/shared/EmptyState';

interface Stat {
  label: string;
  value: string;
  color: string;
}

export function AnalyticsDashboard() {
  const calculations = useAppStore((s) => s.calculations);

  const stats = useMemo(() => {
    if (calculations.length === 0) return null;

    const totalCalc = calculations.length;

    // Mode distribution
    const modeCounts: Record<string, number> = {};
    for (const c of calculations) {
      const mode = c.shipment.mode;
      modeCounts[mode] = (modeCounts[mode] || 0) + 1;
    }

    // Total packages across all calculations
    const totalPackages = calculations.reduce(
      (sum, c) => sum + c.shipment.packages.reduce((pSum: number, p) => pSum + p.quantity, 0),
      0
    );

    // Average chargeable weight
    const avgCW = calculations.reduce((sum, c) => sum + c.summary.chargeableWeightKg, 0) / totalCalc;

    // Average CBM
    const avgCBM = calculations.reduce((sum, c) => sum + c.summary.totalCBM, 0) / totalCalc;

    const modeStats: Stat[] = Object.entries(modeCounts)
      .sort((a, b) => b[1] - a[1])
      .map(([mode, count]) => ({
        label: mode.charAt(0).toUpperCase() + mode.slice(1),
        value: `${count} (${Math.round((count / totalCalc) * 100)}%)`,
        color: mode === 'air' ? 'var(--color-accent)' : mode === 'ocean' ? 'var(--color-info)' : 'var(--color-warning)',
      }));

    return {
      totalCalc,
      totalPackages,
      avgCW: avgCW.toFixed(1),
      avgCBM: avgCBM.toFixed(3),
      modeStats,
      maxMode: modeStats[0]?.label ?? 'N/A',
    };
  }, [calculations]);

  if (!stats) {
    return (
      <div className="card">
        <EmptyState
          icon={BarChartIcon}
          compact
          title="No analytics yet"
          description="Save calculations to unlock totals, averages, and mode distribution across your shipment history."
        />
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
      <h3 className="section-title">Analytics Overview</h3>

      {/* Key metrics */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 'var(--space-3)' }}>
        {[
          { label: 'Total Calculations', value: String(stats.totalCalc), color: 'var(--color-accent)' },
          { label: 'Total Packages', value: String(stats.totalPackages), color: 'var(--color-success)' },
          { label: 'Avg Chargeable Wt', value: `${stats.avgCW} kg`, color: 'var(--color-warning)' },
          { label: 'Avg CBM', value: `${stats.avgCBM} m³`, color: 'var(--color-info)' },
          { label: 'Most Used Mode', value: stats.maxMode, color: 'var(--color-accent)' },
        ].map((stat) => (
          <div
            key={stat.label}
            className="card"
            style={{ textAlign: 'center', padding: 'var(--space-3)' }}
          >
            <div style={{ fontSize: 'var(--font-size-2xl)', fontWeight: 'var(--font-weight-bold)', color: stat.color }}>
              {stat.value}
            </div>
            <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-secondary)', marginTop: 'var(--space-1)' }}>
              {stat.label}
            </div>
          </div>
        ))}
      </div>

      {/* Mode distribution bar */}
      {stats.modeStats.length > 0 && (
        <div className="card" style={{ padding: 'var(--space-4)' }}>
          <h4 style={{ fontSize: 'var(--font-size-sm)', fontWeight: 'var(--font-weight-semibold)', marginBottom: 'var(--space-3)' }}>
            Mode Distribution
          </h4>
          <div style={{ display: 'flex', borderRadius: 'var(--radius-md)', overflow: 'hidden', height: 24 }}>
            {stats.modeStats.map((mode) => (
              <div
                key={mode.label}
                style={{
                  flex: 1,
                  background: mode.color,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 'var(--font-size-xs)',
                  color: 'var(--color-text-inverse)',
                  fontWeight: 'var(--font-weight-semibold)',
                  minWidth: 60,
                }}
                title={`${mode.label}: ${mode.value}`}
              >
                {mode.label}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
